import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Callout,
  Card,
  DateField,
  Modal,
  SelectField,
  TextArea,
  TextField,
  type SelectFieldOption,
} from '@humanforce/mango-ui-react';
import {
  createPayRun,
  listBusinessRecordReferences,
  listPayGroupReferences,
  listPayPeriodReferences,
  PayRunRequestError,
} from './payRunsHttpClient';
import type {
  BusinessRecordReference,
  CreatePayRunRequest,
  PayGroupReference,
  PayPeriodReference,
  PayRunFieldError,
} from './payRunsApiTypes';

export type NewPayRunCloseReason = 'cancelled' | 'created';

interface NewPayRunModalProps {
  open: boolean;
  onClose: (reason: NewPayRunCloseReason, createdPayId?: string) => void;
}

const STANDARD = 'standard' as const;

type ReferenceLoadError = {
  source: 'business_records' | 'pay_groups' | 'pay_periods';
  message: string;
};

const referenceErrorTitles: Record<ReferenceLoadError['source'], string> = {
  business_records: 'Business Records unavailable',
  pay_groups: 'Pay Groups unavailable',
  pay_periods: 'Pay Periods unavailable',
};

const asSingleValue = (value: unknown | unknown[] | null): string =>
  typeof value === 'string' ? value : '';

const options = <T,>(
  values: T[],
  getValue: (value: T) => string,
  getLabel: (value: T) => string,
  isDisabled?: (value: T) => boolean,
): SelectFieldOption[] => values.map((value) => ({
  value: getValue(value),
  label: getLabel(value),
  disabled: isDisabled?.(value),
}));

const fieldMessage = (errors: PayRunFieldError[], field: string): string | undefined =>
  errors.find((error) => error.field === field)?.message;

export const NewPayRunModal = ({ open, onClose }: NewPayRunModalProps) => {
  const [businessRecords, setBusinessRecords] = useState<BusinessRecordReference[]>([]);
  const [payGroups, setPayGroups] = useState<PayGroupReference[]>([]);
  const [payPeriods, setPayPeriods] = useState<PayPeriodReference[]>([]);
  const [businessRecordId, setBusinessRecordId] = useState('');
  const [payGroupId, setPayGroupId] = useState('');
  const [payPeriodId, setPayPeriodId] = useState('');
  const [displayLabel, setDisplayLabel] = useState('');
  const [notes, setNotes] = useState('');
  const [loadingBusinessRecords, setLoadingBusinessRecords] = useState(false);
  const [loadingPayGroups, setLoadingPayGroups] = useState(false);
  const [loadingPayPeriods, setLoadingPayPeriods] = useState(false);
  const [referenceError, setReferenceError] = useState<ReferenceLoadError | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<PayRunFieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const idempotencyRef = useRef<{ signature: string; key: string } | null>(null);
  const payGroupRequestSequence = useRef(0);
  const payPeriodRequestSequence = useRef(0);

  const selectedPeriod = useMemo(
    () => payPeriods.find((period) => period.payPeriodId === payPeriodId) ?? null,
    [payPeriodId, payPeriods],
  );
  const notesError = notes.length > 500 ? 'Notes must be 500 characters or fewer.' : fieldMessage(fieldErrors, 'notes');
  const valid = Boolean(businessRecordId && payGroupId && selectedPeriod?.available && !notesError);

  useEffect(() => {
    if (!open) return;
    const labelDialog = () => {
      for (const dialog of document.querySelectorAll('[role="dialog"]')) {
        if (dialog.querySelector('.mgo-modal__title')?.textContent === 'Create New Pay Run')
          dialog.setAttribute('aria-label', 'Create New Pay Run');
      }
    };
    labelDialog();
    const observer = new MutationObserver(labelDialog);
    observer.observe(document.body, { childList: true, subtree: true });
    let active = true;
    setBusinessRecords([]);
    setPayGroups([]);
    setPayPeriods([]);
    setLoadingPayGroups(false);
    setLoadingPayPeriods(false);
    setBusinessRecordId('');
    setPayGroupId('');
    setPayPeriodId('');
    setDisplayLabel('');
    setNotes('');
    setReferenceError(null);
    setSubmitError(null);
    setFieldErrors([]);
    idempotencyRef.current = null;
    payGroupRequestSequence.current += 1;
    payPeriodRequestSequence.current += 1;
    setLoadingBusinessRecords(true);

    void listBusinessRecordReferences()
      .then((records) => {
        if (active) setBusinessRecords(records);
      })
      .catch((error: unknown) => {
        if (active) {
          setReferenceError({
            source: 'business_records',
            message: error instanceof Error ? error.message : 'Business Records could not be loaded.',
          });
        }
      })
      .finally(() => {
        if (active) setLoadingBusinessRecords(false);
      });

    return () => {
      active = false;
      observer.disconnect();
    };
  }, [open]);

  const selectBusinessRecord = (value: unknown | unknown[] | null) => {
    const nextId = asSingleValue(value);
    setBusinessRecordId(nextId);
    setPayGroupId('');
    setPayPeriodId('');
    setPayGroups([]);
    setPayPeriods([]);
    setReferenceError(null);
    setSubmitError(null);
    setFieldErrors([]);
    const sequence = ++payGroupRequestSequence.current;
    payPeriodRequestSequence.current += 1;
    if (!nextId) return;

    setLoadingPayGroups(true);
    void listPayGroupReferences(nextId)
      .then((groups) => {
        if (sequence === payGroupRequestSequence.current) setPayGroups(groups);
      })
      .catch((error: unknown) => {
        if (sequence === payGroupRequestSequence.current) {
          setReferenceError({
            source: 'pay_groups',
            message: error instanceof Error ? error.message : 'Pay Groups could not be loaded.',
          });
        }
      })
      .finally(() => {
        if (sequence === payGroupRequestSequence.current) setLoadingPayGroups(false);
      });
  };

  const selectPayGroup = (value: unknown | unknown[] | null) => {
    const nextId = asSingleValue(value);
    setPayGroupId(nextId);
    setPayPeriodId('');
    setPayPeriods([]);
    setLoadingPayPeriods(false);
    setReferenceError(null);
    setSubmitError(null);
    setFieldErrors([]);
    const sequence = ++payPeriodRequestSequence.current;
    if (!businessRecordId || !nextId) return;

    setLoadingPayPeriods(true);
    void listPayPeriodReferences(businessRecordId, nextId, STANDARD)
      .then((periods) => {
        if (sequence === payPeriodRequestSequence.current) setPayPeriods(periods);
      })
      .catch((error: unknown) => {
        if (sequence === payPeriodRequestSequence.current) {
          setReferenceError({
            source: 'pay_periods',
            message: error instanceof Error ? error.message : 'Pay Periods could not be loaded.',
          });
        }
      })
      .finally(() => {
        if (sequence === payPeriodRequestSequence.current) setLoadingPayPeriods(false);
      });
  };

  const submit = async () => {
    if (!valid || submittingRef.current) return;
    const request: CreatePayRunRequest = {
      businessRecordId,
      payGroupId,
      payPeriodId,
      runType: STANDARD,
      displayLabel: displayLabel.trim() || null,
      notes: notes.trim() || null,
    };
    const signature = JSON.stringify(request);
    if (idempotencyRef.current?.signature !== signature)
      idempotencyRef.current = { signature, key: crypto.randomUUID() };

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors([]);
    try {
      const created = await createPayRun(request, idempotencyRef.current.key);
      onClose('created', created.payId);
    } catch (error) {
      if (error instanceof PayRunRequestError) {
        setSubmitError(error.message);
        setFieldErrors(error.fieldErrors);
      } else {
        setSubmitError(error instanceof Error ? error.message : 'The Pay Run could not be created.');
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const cancel = () => {
    if (!submittingRef.current) onClose('cancelled');
  };

  const noBusinessRecords = !loadingBusinessRecords
    && referenceError?.source !== 'business_records'
    && businessRecords.length === 0;
  const noPayGroups = Boolean(
    businessRecordId
    && !loadingPayGroups
    && referenceError?.source !== 'pay_groups'
    && payGroups.length === 0,
  );
  const noAvailablePeriods = Boolean(
    payGroupId
    && !loadingPayPeriods
    && referenceError?.source !== 'pay_periods'
    && payPeriods.every((period) => !period.available),
  );

  return (
    <Modal
      visible={open}
      header="Create New Pay Run"
      size="large"
      closable={!submitting}
      dismissableMask={!submitting}
      closeOnEscape={!submitting}
      onHide={cancel}
      testId="new-pay-run-dialog"
      footer={(
        <>
          <Button label="Cancel" variant="secondary" disabled={submitting} onClick={cancel} />
          <Button label="Create Pay Run" variant="primary" loading={submitting} disabled={!valid} onClick={() => void submit()} />
        </>
      )}
    >
      <form className="mgo-stack mgo-gap-xl" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        {referenceError && (
          <Callout tone="critical" title={referenceErrorTitles[referenceError.source]}>
            {referenceError.message}
          </Callout>
        )}
        {submitError && <Callout tone="critical" title="Pay Run not created">{submitError}</Callout>}

        <div className="mgo-grid mgo-grid-cols-tablet-2 mgo-gap-l">
          <Card
            header={(
              <div className="mgo-stack mgo-gap-xs">
                <span className="mgo-text-m mgo-weight-strong">Run type &amp; pay period</span>
                <span className="mgo-text-xs mgo-color-muted">Choose the payroll references for this run.</span>
              </div>
            )}
          >
            <div className="mgo-stack mgo-gap-l">
              <TextField label="Run Type" value="Standard — regular cycle pay" readOnly />
              <SelectField
                label="Business Record"
                required
                value={businessRecordId || null}
                options={options(businessRecords, (record) => record.businessRecordId, (record) => record.displayName)}
                placeholder={loadingBusinessRecords ? 'Loading Business Records…' : 'Select a Business Record'}
                loading={loadingBusinessRecords}
                disabled={loadingBusinessRecords || referenceError?.source === 'business_records'}
                searchable={false}
                status={fieldMessage(fieldErrors, 'businessRecordId') ? 'critical' : ''}
                statusText={fieldMessage(fieldErrors, 'businessRecordId')}
                onChange={selectBusinessRecord}
              />
              <SelectField
                label="Pay Group"
                required
                value={payGroupId || null}
                options={options(payGroups, (group) => group.payGroupId, (group) => group.name)}
                placeholder={loadingPayGroups ? 'Loading Pay Groups…' : 'Select a Pay Group'}
                loading={loadingPayGroups}
                disabled={!businessRecordId || loadingPayGroups}
                searchable={false}
                status={fieldMessage(fieldErrors, 'payGroupId') ? 'critical' : ''}
                statusText={fieldMessage(fieldErrors, 'payGroupId')}
                onChange={selectPayGroup}
              />
              <SelectField
                label="Pay Period"
                required
                value={payPeriodId || null}
                options={options(
                  payPeriods,
                  (period) => period.payPeriodId,
                  (period) => period.periodLabel || `${period.periodStartDate} – ${period.periodEndDate}`,
                  (period) => !period.available,
                )}
                placeholder={loadingPayPeriods ? 'Loading Pay Periods…' : 'Select a Pay Period'}
                loading={loadingPayPeriods}
                disabled={!payGroupId || loadingPayPeriods || noAvailablePeriods}
                searchable={false}
                status={fieldMessage(fieldErrors, 'payPeriodId') ? 'critical' : ''}
                statusText={fieldMessage(fieldErrors, 'payPeriodId')}
                onChange={(value) => {
                  setPayPeriodId(asSingleValue(value));
                  setSubmitError(null);
                  setFieldErrors([]);
                }}
              />

              {noBusinessRecords && <Callout tone="neutral">No active Business Records with Pay Groups are available.</Callout>}
              {noPayGroups && <Callout tone="neutral">This Business Record has no active Pay Groups.</Callout>}
              {noAvailablePeriods && <Callout tone="neutral">This Pay Group has no available Pay Periods for a Standard Pay Run.</Callout>}

              <div className="mgo-grid mgo-grid-cols-tablet-2 mgo-gap-m">
                <DateField label="Period Start" dateFormat="ymd" value={selectedPeriod?.periodStartDate ?? ''} readOnly fullWidth />
                <DateField label="Period End" dateFormat="ymd" value={selectedPeriod?.periodEndDate ?? ''} readOnly fullWidth />
              </div>
              <DateField label="Payment Date" dateFormat="ymd" value={selectedPeriod?.paymentDate ?? ''} readOnly fullWidth />
            </div>
          </Card>

          <Card
            header={(
              <div className="mgo-stack mgo-gap-xs">
                <span className="mgo-text-m mgo-weight-strong">Pay Run details</span>
                <span className="mgo-text-xs mgo-color-muted">Add optional context for payroll reviewers.</span>
              </div>
            )}
          >
            <div className="mgo-stack mgo-gap-l">
              <TextField
                label="Display Label"
                placeholder="Optional name for this Pay Run"
                value={displayLabel}
                status={fieldMessage(fieldErrors, 'displayLabel') ? 'critical' : undefined}
                statusText={fieldMessage(fieldErrors, 'displayLabel')}
                onChange={(event) => {
                  setDisplayLabel(event.target.value);
                  setSubmitError(null);
                  setFieldErrors((errors) => errors.filter((error) => error.field !== 'displayLabel'));
                }}
              />
              <TextArea
                label="Notes"
                placeholder="Anything payroll reviewers should know about this run…"
                value={notes}
                rows={8}
                resize="vertical"
                characterLimit={500}
                status={notesError ? 'critical' : ''}
                statusText={notesError}
                onChange={(event) => {
                  setNotes(event.target.value);
                  setSubmitError(null);
                  setFieldErrors((errors) => errors.filter((error) => error.field !== 'notes'));
                }}
              />
              <Callout tone="info" title="Standard Pay Run">
                Dates and availability come from the selected Pay Period. Calculation, approval and output controls are added in later workflow stages.
              </Callout>
            </div>
          </Card>
        </div>
      </form>
    </Modal>
  );
};
